/**
 * Wire and view types for the Troubleshooting page.
 *
 * Two kinds of type live here:
 *  - Shapes the current admin API already returns (WireGuard tunnels, site
 *    names, site about, SMTP instructions). These import from the owning page.
 *  - PROPOSED contracts for endpoints that do not exist yet. Each is marked
 *    `@gap` with the endpoint the UI calls. The data layer returns
 *    `{ status: "unavailable" }` for those until the backend lands, and the UI
 *    renders an explicit "not connected yet" card rather than guessing.
 *    The full list, with reasons, is in docs/troubleshooting-api-gaps.md.
 */
import { WireGuardTunnel } from "../WireGuardTunnels/wireGuardTunnelsInterfaces";

export type { WireGuardTunnel };

export type TroubleshootingTab =
  | "diagnosis"
  | "connections"
  | "wireguard"
  | "smtp-cameras"
  | "alarm-receivers"
  /** Staff only (ENG-2761): fish2pano panorama parameters for fisheye cameras. */
  | "fisheye-dewarp";

export type WireGuardSubTab = "peer-health" | "debugger";

/**
 * Scope narrows every tab the same way, in three steps: the whole account,
 * one parent group, or one site inside it. Each tab keeps its own scope and
 * it resets when you leave the tab — the site lists differ per tab, so a
 * selection carried across would often name a site that tab cannot show.
 */
export type ScopeMode = "all" | "group" | "site";

/** A parent account the user can see; the middle step of the scope picker. */
export interface ParentAccount {
  id: number;
  name: string;
}

/** `GET customer/names/{groupId}/` row (only the fields this page reads). */
export interface TroubleshootingSite {
  id: number;
  name: string;
  /** Parent account the site was listed under; used for group-scoped calls. */
  groupId: number;
}

/**
 * The subset of `GET customer/{id}/about/` this page needs to say how a site
 * connects to Actuate. Optional fields are optional on the wire too.
 */
export interface SiteConnectionDetail {
  id: number;
  name: string;
  integration_type_name: string | null;
  /** WireGuard tunnel id when the site is reached through a tunnel. */
  wireguard: number | null;
  server_ip: string | null;
  server_port: number | null;
  stream_port: number | null;
  management_server_ip: string | null;
  event_server_ip: string | null;
  /** Milestone: the event server port the connector sends alarms to. */
  alert_port?: number | null;
  /** Milestone management server ports. */
  milestone_http_port?: number | null;
  milestone_ssl_port?: number | null;
  /** Digital Watchdog: the Nx cloud system id when the site goes through Nx's relay. */
  cloud_system_id?: string | null;
  /** Digital Watchdog: every call goes through the Nx cloud relay. */
  use_nx_proxy?: boolean | null;
  /** OpenEye: the connector goes through OpenEye's cloud (OWS) for this NVR ID. */
  openeye_use_ows?: boolean | null;
  openeye_nvr_id?: string | null;
  /**
   * The auto-add integration ("openeye", "hikcentral", …). connector-tools'
   * camera-list job picks its login by this for most integrations.
   */
  auto_add?: string | null;
  /**
   * Saved site login. admin returns the password only to users with
   * view_passwords; everyone else gets PASSWORD_MASK (or null when empty).
   */
  username?: string | null;
  password?: string | null;
  /**
   * The connector leaves through Actuate's NAT (one fixed address customers
   * can allowlist). False: it leaves from whichever public node it runs on,
   * a changing address, so a NAT probe is not what the connector sees.
   */
  ecs_nat_instance?: boolean;
  read_filename?: boolean;
  read_filename_expression?: string | null;
  armed?: boolean;
  active?: boolean;
  /** actuate_admin MotionStatus, e.g. OK, NOT_RUNNING, DISARMED_DURING_SCHEDULE. */
  armed_status?: string;
  /**
   * "Test stream" can run for this site's cameras: switched on and a video
   * site the test supports (ENG-2470). Missing on an older admin: hide it.
   */
  stream_test_available?: boolean;
  /** Customer.live_streaming_enabled: the stream mint's site gate (ENG-2470). */
  live_streaming_enabled?: boolean | null;
  /**
   * "Open device" (remote device sessions, ENG-2470 Phase 2) is switched on
   * for this site and this user. Missing on an older admin: hide it.
   */
  device_session_available?: boolean;
  /** "Open NVR" / "Open recorder" for the site's server (ENG-2507). */
  server_open_device_available?: boolean;
}

export type ConnectionKind =
  | "wireguard"
  | "site-to-site"
  | "direct"
  | "smtp"
  | "cloud"
  | "unknown";

/** `GET customer/{id}/instructions/` — SMTP settings the customer types into the NVR. */
export interface SmtpInstructions {
  id: number;
  name: string;
  show_instructions: boolean;
  server_address: string;
  server_port: number | string;
  recipient: string;
  cameras: { camera_name: string; recipient: string }[];
  message?: string;
}

// ---------------------------------------------------------------------------
// Result wrapper for endpoints that may not exist yet
// ---------------------------------------------------------------------------

export type GapResult<T> =
  | { status: "ok"; data: T; sample?: false }
  | { status: "ok"; data: T; sample: true }
  | { status: "unavailable"; endpoint: string; reason: string }
  | {
      status: "error";
      endpoint: string;
      message: string;
      /** A 429's wait, from `retry_after` or the Retry-After header. */
      retryAfterSeconds?: number;
      /**
       * The server answered and said no (400/403/409/429): the request was
       * understood and refused, as opposed to a network or server failure.
       */
      refused?: boolean;
    };

// ---------------------------------------------------------------------------
// Site-to-Site VPN — `GET network_path/` + `/{id}/health/` (ENG-1521, admin #2877)
// ---------------------------------------------------------------------------

export type TunnelState = "UP" | "DOWN" | "UNKNOWN";

export type NetworkPathKind = "aws_site_to_site" | "wireguard" | "direct";

/** The list's cheap glance; `health/` is the ladder. */
export interface NetworkPathStatus {
  status: "up" | "degraded" | "down" | "unknown" | "disabled";
  tunnels_up: number | null;
  tunnels_total: number | null;
}

/**
 * One Site-to-Site path as this page shows it. Mapped from a `network_path/`
 * row: the list carries a per-path status glance, not per-tunnel telemetry,
 * so `tunnels` is synthesised from `tunnels_up / tunnels_total` and the
 * ladder (`health/`) is where the detail lives.
 */
export interface SiteToSiteConnection {
  id: string;
  name: string;
  kind: NetworkPathKind;
  /** Customer-side IPsec peer address. */
  customer_gateway_ip: string;
  /** e.g. "AWS Site-to-Site VPN (IPsec) us-east-1" */
  actuate_gateway: string;
  status: NetworkPathStatus;
  tunnels: {
    outside_ip: string;
    state: TunnelState;
    status_message: string;
    last_status_change: string | null;
  }[];
  /** What the health ladder probes through the path, if configured. */
  probe_target: string | null;
  probe_port: number | null;
  probe_label: string | null;
  /** Accepted route prefixes toward the customer (not on the list; the ladder checks them). */
  routes: string[];
  /**
   * Sites that ride the path. Exact when the API sent `customer_ids`
   * (ENG-1674, admin #2953); otherwise every site under the path's parent
   * account, best effort, like tunnels.
   */
  site_ids: number[];
  /** True when `site_ids` came from the API rather than the group fallback. */
  site_ids_exact?: boolean;
  /** For a `wireguard` path: the tunnel row it wraps. */
  wireguard_tunnel?: number | null;
}

export type SiteToSiteRungId =
  | "tunnel_status"
  | "route_present"
  | "tunnel_reachability"
  | "vms_connectivity";

/** One rung of `GET network_path/{id}/health/`. */
export interface SiteToSiteCheckRung {
  id: SiteToSiteRungId;
  title: string;
  status: "pass" | "fail" | "warn" | "skipped";
  summary: string;
  diagnosis?: string | null;
  next_action?: string | null;
}

export interface SiteToSiteClassification {
  code:
    | "tunnel_down"
    | "degraded"
    | "routing_firewall"
    | "service_unreachable"
    | "healthy"
    | "inconclusive";
  label: string;
  owner: "customer" | "actuate" | "unknown";
  confidence: "high" | "medium" | "low";
  summary: string;
}

export interface SiteToSiteCheckResult {
  rungs: SiteToSiteCheckRung[];
  classification: SiteToSiteClassification;
  checked_at: string | null;
}

// ---------------------------------------------------------------------------
// WireGuard telemetry — `GET wireguard_tunnel/telemetry/` (ENG-1668)
// ---------------------------------------------------------------------------

/** One row per visible tunnel; every field but tunnel_id is null until the gateway has sampled the peer. */
export interface WireGuardPeerTelemetry {
  tunnel_id: number;
  /** Customer endpoint as seen by the gateway, host:port. */
  endpoint: string | null;
  persistent_keepalive_s: number | null;
  rx_bytes_10m: number | null;
  tx_bytes_10m: number | null;
  /** True when the handshake is stale but handshake-sized traffic still arrives (counters heuristic). */
  initiations_without_handshake: boolean | null;
  /** Seconds between the two samples the *_10m deltas span (~600). */
  window_seconds?: number | null;
  rx_bytes?: number | null;
  tx_bytes?: number | null;
  sampled_at?: string | null;
}

// ---------------------------------------------------------------------------
// Connectivity analyzer / credential test  @gap
// ---------------------------------------------------------------------------

export type PortProbeResult = "open" | "closed" | "filtered" | "error";

export type ProbeOrigin =
  | { kind: "nat" }
  | { kind: "wireguard"; tunnel_id: number }
  | { kind: "site-to-site"; connection_id: string };

/** @gap POST network-diagnostics/port-check/ */
export interface PortCheckRequest {
  host: string;
  ports: number[];
  from: ProbeOrigin;
}
export interface PortCheckResponse {
  host: string;
  from_label: string;
  ping: { reply: boolean; ms: number | null } | null;
  results: {
    port: number;
    result: PortProbeResult;
    ms: number | null;
    detail?: string | null;
  }[];
  checked_at: string;
  /** What the origin resolved the host to; null on a DNS failure. */
  resolved_ip?: string | null;
}

/** `POST network-credential-check/` — actuate_admin PR #2928 (ENG-1670). Synchronous. */
export interface CredentialCheckRequest {
  host: string;
  /** ONVIF / HTTP port. */
  port: number;
  /** RTSP port for the DESCRIBE attempt; the API defaults to 554. */
  rtsp_port?: number;
  username: string;
  password: string;
  from: ProbeOrigin;
}
export interface CredentialCheckResponse {
  tcp: { ok: boolean; ms: number | null };
  onvif: { ok: boolean; http_status: number | null; detail: string };
  rtsp: { ok: boolean; rtsp_status: number | null; detail: string };
  device?: { manufacturer?: string; model?: string; firmware?: string };
  /** Where the attempt ran from, in the API's words. */
  from_label?: string;
  /** Attempts left against this host in the current 10-minute window (device lockout guard). */
  attempts_remaining?: number;
  resolved_ip?: string | null;
  checked_at?: string;
}

// ---------------------------------------------------------------------------
// SMTP  @gap (partly)
// ---------------------------------------------------------------------------

/** GET customer/{id}/smtp_filenames/ — one attachment filename the receiver has seen (contract in tabs/SmtpFilenameDistribution.tsx). */
export interface SmtpFilenameSample {
  filename: string;
  count_12h?: number;
  last_seen?: string | null;
  extracted_camera_name?: string | null;
  suggested_camera_name?: string | null;
  extracted_matches_existing_camera_id?: number | null;
  suggested_matches_existing_camera_id?: number | null;
}
export interface SmtpFilenamesResponse {
  samples: SmtpFilenameSample[];
  read_filename: boolean;
  read_filename_expression: string;
  suggested_expression: string;
}

/** @gap GET customer/{id}/smtp_recent/?since=<iso> — "did my test email arrive?" */
export interface SmtpRecentEmail {
  received_at: string;
  from_address: string;
  sending_ip: string | null;
  tls: boolean | null;
  subject: string | null;
  attachment_filename: string | null;
  attachment_bytes: number | null;
  /**
   * The camera the email was routed to; false or "" when no camera matched.
   * null or missing means the API did not say, which is not the same thing.
   */
  camera_identified?: string | false | null;
  /** false: not analysed. null or missing: the API did not say. */
  analysed?: boolean | null;
}
export interface SmtpRecentResponse {
  emails: SmtpRecentEmail[];
  /** Receiver-wide heartbeat so "nothing arrived" can be told apart from "receiver down". */
  receiver_accepting: boolean;
  receiver_emails_10m: number | null;
  last_email_from_site: string | null;
}

// ---------------------------------------------------------------------------
// Alarm receivers  @gap
// ---------------------------------------------------------------------------

export type ReceiverType =
  | "immix"
  | "dice"
  | "sentinel"
  | "milestone_event_server"
  | "webhook"
  | "sureview"
  | "other";

/** @gap GET alarm-receivers/?group_id=  — one row per distinct destination host:port across the group's cameras */
export interface AlarmReceiver {
  id: string;
  type: ReceiverType;
  label: string;
  host: string;
  port: number;
  /** Connection the receiver is reached through, when not public. */
  via_connection: {
    kind: ConnectionKind;
    id: string | number;
    name: string;
  } | null;
  site_ids: number[];
  health: {
    listening:
      | "listening"
      | "refused"
      | "timed_out"
      | "unreachable"
      | "unknown";
    detail: string;
    last_accepted_at: string | null;
    failures_last_hour: number | null;
  };
}

/** @gap POST alarm-receivers/{id}/test/ ; GET alarm-receivers/tests/?group_id= */
export interface ReceiverTestRecord {
  id: string;
  receiver_id: string;
  sent_at: string;
  result: "delivered" | "failed" | "pending";
  detail: string;
  sent_by: string;
}

// ---------------------------------------------------------------------------
// Findings (built client-side today, ideally server-side later)
// ---------------------------------------------------------------------------

/**
 * `info`: an explanation, not a fault (a disarmed or inactive site with
 * nothing else wrong). No warning icon and no "fix" wording (Jacob).
 */
export type FindingSeverity = "bad" | "warn" | "good" | "info";
export type FindingOwner =
  | "customer"
  | "actuate"
  | "shared"
  | "self-serve"
  /** The VMS vendor or the VMS's own settings (camera connectivity check, ENG-2457). */
  | "vms"
  /** Not a fault: nothing to fix. */
  | "none";
/**
 * `unreachable`: one directly connected site has gone silent with nothing
 * else to blame — no tunnel, path or receiver check applies — so the cameras
 * themselves are the most likely cause. `healthy` is the all-clear card, which
 * may itself be unconfirmed (severity `warn`) when no check covered the scope.
 */
export type FindingKind =
  | "connection"
  | "receiver"
  | "smtp"
  | "unreachable"
  | "arming"
  | "healthy"
  /** A camera setting that degrades a feature without stopping alarms (ENG-2222). */
  | "camera"
  /** The connector's VMS login is failing, with the VMS's own answer (ENG-2252). */
  | "login"
  /** Cameras that stop at the same step of the camera connectivity check (ENG-2457). */
  | "stream";

export interface Finding {
  id: string;
  severity: FindingSeverity;
  owner: FindingOwner;
  kind: FindingKind;
  connectionKind?: ConnectionKind;
  /** True when one receiver serves every site, so a fault is fleet-wide. */
  global?: boolean;
  title: string;
  /** For display: in the viewer's locale. */
  since: string | null;
  /** The same moment as ISO, for the copied report (QA DG-35). */
  sinceIso?: string | null;
  body: string;
  steps: string[];
  affectedSiteIds: number[];
  /** Big number + caption on the right of the card. */
  metric: { value: string; label: string };
  actions: FindingAction[];
  /** For "Open in WireGuard Debugger" — which tunnel to preselect. */
  tunnelId?: number;
  /** For "Open connection check". */
  connectionId?: string;
  /** For "Send test alarm". */
  receiverId?: string;
  /** For "Fix filename rule". */
  siteId?: number;
  /** For "Check reachability" — host to preload into the Connectivity analyzer. */
  probeHost?: string;
  /** Ports the site's settings name for that host, preloaded with it. */
  probePorts?: number[];
  /** Cameras the finding is about; their saved login backs up the site's. */
  probeCameraIds?: number[];
  /** With siteId: the site's name as it appears in `title`, rendered as a link to its page. */
  siteName?: string;
  /** Raw evidence shown in monospace under the body (e.g. the VMS's error). */
  detailLine?: string;
}

/** "Check reachability" hand-off from a finding to the Connections tab. */
export interface ProbePrefill {
  host: string;
  ports?: number[];
  /** Site whose saved settings fill the Credential test. */
  siteId?: number | null;
  /**
   * That site's detail as loaded where the button was pressed. Connections
   * has no scope of its own, so switching to it clears the store's
   * siteDetail; the hand-off carries its own copy.
   */
  site?: SiteConnectionDetail | null;
  cameraIds?: number[];
}

/** What admin sends in place of a secret the caller may not see. */
export const PASSWORD_MASK = "•••••••••";

export type FindingAction =
  | "affected"
  | "test-alarm"
  | "wg-debug"
  | "s2s-check"
  | "fix-smtp"
  | "probe-host"
  | "open-site"
  | "copy";

export type SymptomId = "none" | "late" | "new" | "cam" | "armed";

// ---------------------------------------------------------------------------
// Alerts received — monitoring-api `alert/` (ENG-1902)
// ---------------------------------------------------------------------------

/** One recent alert, the fields the Diagnosis card and report read. */
export interface AlertReceivedRow {
  at: string;
  siteName: string;
  cameraName: string;
  sitePk: number | null;
  cameraPk: number | null;
}

/**
 * "We processed N alerts for your scope in the last hour, latest … from …".
 * The first thing a customer asking "am I receiving alarms?" needs, before any
 * finding: whether alarms are flowing at all right now.
 */
export interface AlertsReceived {
  windowHours: number;
  /** Start of the window, ISO. */
  since: string;
  count: number;
  /**
   * The API returns at most `limit` rows and no total, so when the page came
   * back full `count` is a floor, not the number — the UI says "at least".
   */
  truncated: boolean;
  latest: AlertReceivedRow | null;
  /** Distinct cameras that alerted in the window — the seed for a per-camera "has it alarmed" view. */
  camerasAlerted: number;
  /** Newest alert per camera pk, ISO; only cameras seen in the window. */
  latestByCamera: Record<number, string>;
}

/** One camera at a site and when it last alarmed (ENG-1921). */
export interface CameraLastAlarmRow {
  cameraPk: number;
  cameraName: string;
  /** ISO of the newest alert in the window, or null — the camera was quiet. */
  lastAt: string | null;
  count: number;
}

/**
 * Per-camera "has it alarmed?" for one site — the read a monitoring centre
 * needs before closing a service ticket, in place of a mass test alarm.
 * Cameras are listed quiet-first.
 */
export interface CameraLastAlarms {
  siteId: number;
  windowHours: number;
  /** Start of the window the rows actually cover, ISO. */
  since: string;
  rows: CameraLastAlarmRow[];
  quiet: number;
  /**
   * The API returned its maximum page (newest first), so alerts before
   * `since` were not read; `since` is then the oldest alert we saw, not the
   * requested window start, and a camera quiet since before it may show as
   * quiet here.
   */
  truncated: boolean;
}

/**
 * One camera's test alarm from a per-camera site test (ENG-2633): admin's
 * `alarm-receivers/test-site/{site}` sends each camera its own event with its
 * own recipient and input1, so the monitoring station sees every camera.
 */
export interface SiteCameraTestRow {
  testId: string;
  cameraId: number;
  cameraName: string;
  receiverId: string;
  sentAt: string;
  result: ReceiverTestRecord["result"];
  detail: string;
}

/** A per-camera site test run and, as they arrive, its outcomes. */
export interface SiteCameraTestRun {
  siteId: number;
  batchId: string;
  /** "quiet" = only the cameras with no alarm in the window; "all" = every camera with an alert. */
  mode: "quiet" | "all";
  sentAt: string;
  rows: SiteCameraTestRow[];
  skipped: { cameraId: number; cameraName: string; reason: string }[];
  /** Still waiting on outcomes. */
  polling: boolean;
}

// ---------------------------------------------------------------------------
// "Did we change anything?" — customer/{id}/changes/ (ENG-1840)
// ---------------------------------------------------------------------------

/** One path in settings.json that differs between two saved versions. Credentials arrive redacted. */
/**
 * Per-camera "working now" signals for one site, from admin's
 * `customer/{id}/camera_liveness/` (ENG-2046), which reads the connector's
 * DynamoDB rows directly. `signals` says which ones this site's family can
 * produce, so a null value means "no data yet", never "not applicable".
 */
/**
 * The connector's last failed VMS login (ENG-2252), as admin sends it on
 * `camera/{id}/status/` and `camera_liveness/`. Null when there is none, or a
 * later login succeeded.
 */
export interface ConnectionError {
  /** "http_502", "timeout", "connection_refused", "dns", "tls", "no_session", or an exception class name. */
  code: string;
  /** The VMS's own error text, credentials stripped, at most 200 chars. Can be "". */
  detail: string;
  /** ISO UTC of the latest failure. */
  at: string | null;
  /**
   * ISO UTC start of the unbroken failure streak. Admin never sends it null
   * with an error (older rows fall back to `at`); optional here so an admin
   * without it still type-checks, and readers fall back to `at`.
   */
  first_failed_at?: string | null;
  /** "exacq"; other integrations later use their integration_type names. */
  integration: string | null;
  scope: "site" | "camera";
}

export interface CameraLivenessRow {
  cameraId: number;
  cameraName: string;
  /** Address the connector dials for this camera, when admin sends it. */
  host: string | null;
  /**
   * The puller should be receiving frames all the time (not motion-gated
   * Milestone, event-triggered, SMTP, AutoPatrol or clips). Only then does an
   * old last frame mean "down".
   */
  continuous: boolean;
  /** Last frame the puller received: the preview image's upload time (5 min cadence, only on a new frame). */
  lastFrameAt: string | null;
  /** Pull families: the PyAV puller refreshes this while video flows. */
  bandwidthUpdatedAt: string | null;
  inboundKbps: number | null;
  /** SMTP family: last email image received for this camera. */
  lastImageReceivedAt: string | null;
  /** Edge-triggered: true while the camera returns broken frames. */
  brokenFrame: boolean | null;
  /**
   * Measured seconds between keyframes (ENG-2222). Optional: admin's
   * camera_liveness/ does not send it yet (only camera/{id}/status/ does), so
   * it is null until it does.
   */
  keyframeIntervalSeconds?: number | null;
  keyframeIntervalFrames?: number | null;
  keyframeMeasuredAt?: string | null;
  /**
   * True when the interval is a minimum (no second keyframe seen yet);
   * false/null is a measured median or nothing measured (actuate_admin #3070).
   */
  keyframeIntervalIsLowerBound?: boolean | null;
  /** Last failed VMS login for this camera (ENG-2252); null/absent = none. */
  lastConnectionError?: ConnectionError | null;
}
export interface CameraLiveness {
  siteId: number;
  checkedAt: string | null;
  signals: { lastFrame: boolean; bandwidth: boolean; lastImage: boolean };
  /** Which preview bucket answered; off prod it falls back to prod's. */
  lastFrameSource: string | null;
  /** Site heartbeat: the connector reporting (every ~5 min), and census. */
  connector: {
    lastSeenAt: string | null;
    censusRunning: boolean | null;
    inSchedule: boolean | null;
  } | null;
  cameras: CameraLivenessRow[];
  errors: { source: string; detail: string }[];
  /** Newest site-scope VMS login failure across the cameras (ENG-2252). */
  connectionError?: ConnectionError | null;
}

export interface SettingsFieldChange {
  path: string;
  from: unknown;
  to: unknown;
}

export interface SettingsVersion {
  versionId: string;
  savedAt: string;
  isLatest: boolean;
  /** False when the version could not be read; its diff is then everything vs nothing. */
  readable: boolean;
  /** Admin user whose site save landed within two minutes of the upload, if any. */
  savedBy: string | null;
  previousVersionId: string | null;
  changes: SettingsFieldChange[];
}

export interface AdminFieldChange {
  at: string;
  user: string | null;
  field: string;
  from: unknown;
  to: unknown;
  type: string;
}

export interface ConnectorState {
  podBootTime: string | null;
  /** Settings version the running connector reported loading at boot. */
  settingsVersionId: string | null;
  latestSettingsVersionId: string | null;
  latestSettingsSavedAt: string | null;
  /** True when the connector loaded the newest version; null when either side is unknown. */
  runningLatest: boolean | null;
  deployedDate: string | null;
}

/**
 * What *we* changed on a site over the window — settings.json versions with
 * their diffs, admin field edits with who made them, and whether the running
 * connector has picked up the newest settings. Evidence for the "did a restart
 * fix it?" question (SUP-458), so an owner pill is never shown over an
 * unexamined change of ours.
 */
/**
 * The look-back the "Did we change anything?" card asked for (ENG-2244).
 * `hours` is 24, 168 or 720; `sinceLastAlert` asks from the site's last
 * alert, and is `clamped` (sent as the 30-day window) when that alert is
 * older than the 30 days admin keeps.
 */
export type ChangesWindow =
  | { kind: "hours"; hours: number }
  | {
      kind: "sinceLastAlert";
      since: string;
      clamped: boolean;
    };

/**
 * When the site last worked, as admin knows it (ENG-2244). `lastConnectedSource`
 * says how good the connection marker is: `last_frame` is a received video
 * frame; `camera_status` (or unknown) is only a lower bound.
 */
export interface SiteMarkers {
  lastAlertAt: string | null;
  lastConnectedAt: string | null;
  lastConnectedSource: "last_frame" | "camera_status" | null;
}

export interface SiteChanges {
  siteId: number;
  windowHours: number;
  /** The window the card asked for; absent on older callers means 24 h. */
  window?: ChangesWindow;
  since: string;
  until: string;
  /** Admin capped the settings history (newest 100 saves per key). */
  truncated?: boolean;
  markers?: SiteMarkers;
  /** False when the settings bucket could not be listed — an unknown, not "no changes". */
  settingsAvailable: boolean;
  settingsVersions: SettingsVersion[];
  adminChanges: AdminFieldChange[];
  connector: ConnectorState;
}

/** One settings key (site or flex schedule) diffed against an earlier moment. */
export interface SettingsCompareSource {
  flexScheduleId: number | null;
  scheduleName: string | null;
  settingsKey: string;
  baseline: { versionId: string; savedAt: string } | null;
  current: { versionId: string; savedAt: string } | null;
  changes: SettingsFieldChange[];
  identical: boolean;
  /** Why the diff is missing or partial, in admin's words. */
  reason: string | null;
}

/** `customer/{id}/changes/compare/?at=` — settings now vs at `at` (ENG-2244). */
export interface SettingsCompare {
  siteId: number;
  at: string;
  sources: SettingsCompareSource[];
}

/** The camera-list refresh fields of a site's status row (`site_status/`). */
export interface RefreshStatus {
  flex_schedule?: number | null;
  last_refresh_started?: number | string | null;
  last_refresh_completed?: number | string | null;
  /** Plain words, e.g. "Unauthorized, check credentials". Empty on success. */
  connectivity_message?: string | null;
  /** The raw error (credentials redacted by connector-tools). Empty on success. */
  connectivity_reason?: string | null;
}
